import { useEffect, useState } from "react";
import api, { fmtMXN, fmtDate, todayLocal } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Camera, FileDown, CheckCircle2, XCircle, Stamp, FileCheck } from "lucide-react";
import { Modal, In, Sel } from "@/pages/Projects";
import EvidenceGallery from "@/components/EvidenceGallery";

const STATUS_META = {
  capturado: ["CAPTURADO", "#eab308"],
  revisado: ["REVISADO", "var(--cyan)"],
  autorizado: ["AUTORIZADO", "var(--green)"],
  rechazado: ["RECHAZADO", "var(--red)"],
};

export default function GeneratorsTab({ projectId, project, budgetItems, user }) {
  const [gens, setGens] = useState([]);
  const [filter, setFilter] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [gallery, setGallery] = useState(null);
  const [caModal, setCaModal] = useState(null);
  const [caForm, setCaForm] = useState({ reference: "", date: todayLocal(), authorized_by_name: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState([]);

  const isAdmin = user?.role === "admin";
  const isSupervisor = user?.role === "supervisor";
  const canCapture = ["admin", "contador", "auxiliar", "residente", "supervisor"].includes(user?.role);
  const canManage = ["admin", "contador"].includes(user?.role);

  const load = async () => {
    const { data } = await api.get(`/projects/${projectId}/generators`, { params: filter ? { status: filter } : {} });
    setGens(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, filter]);

  const uploadPhotos = async (fl) => {
    const ids = [];
    for (const f of fl) {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd);
      ids.push(data.id);
    }
    return ids;
  };

  const emptyRow = { label: "", length: "", width: "", height: "", pieces: "", qty: "" };
  const startNew = () => {
    setForm({ budget_item_id: budgetItems[0]?.id || "", is_extraordinary: false, code: "", description: "", unit: "", unit_price: "", date: todayLocal(), location: "", notes: "", measurements: [{ ...emptyRow }] });
    setFiles([]);
    setOpenNew(true);
  };

  const rowQty = (m) => {
    const dims = [m.length, m.width, m.height].map(Number).filter((d) => d > 0);
    if (dims.length) return dims.reduce((a, b) => a * b, 1) * (Number(m.pieces) || 1);
    return Number(m.qty) || 0;
  };
  const totalQty = form ? form.measurements.reduce((s, m) => s + rowQty(m), 0) : 0;

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const photos = files.length ? await uploadPhotos(files) : [];
      const payload = {
        budget_item_id: form.is_extraordinary ? null : form.budget_item_id,
        is_extraordinary: form.is_extraordinary,
        code: form.is_extraordinary ? form.code || null : null,
        description: form.is_extraordinary ? form.description || null : null,
        unit: form.is_extraordinary ? form.unit || null : null,
        unit_price: form.is_extraordinary ? Number(form.unit_price || 0) : null,
        date: form.date, location: form.location || null, notes: form.notes || null,
        photos,
        measurements: form.measurements.filter((m) => rowQty(m) > 0).map((m) => ({
          label: m.label || null,
          length: m.length === "" ? null : Number(m.length),
          width: m.width === "" ? null : Number(m.width),
          height: m.height === "" ? null : Number(m.height),
          pieces: m.pieces === "" ? null : Number(m.pieces),
          qty: m.qty === "" ? null : Number(m.qty),
        })),
      };
      await api.post(`/projects/${projectId}/generators`, payload);
      toast.success("Generador capturado");
      setOpenNew(false); load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Error"); }
    setSaving(false);
  };

  const doAction = async (g, action, body = {}) => {
    try {
      await api.post(`/generators/${g.id}/${action}`, body);
      toast.success({ review: "Revisado", authorize: "Autorizado internamente", reject: "Rechazado" }[action] || "OK");
      load();
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Error"); }
  };
  const reject = (g) => {
    const reason = window.prompt(`Motivo del rechazo de ${g.folio}:`);
    if (reason) doAction(g, "reject", { reason });
  };
  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar ${g.folio}?`)) return;
    try { await api.delete(`/generators/${g.id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const saveClientAuth = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/generators/${caModal.id}/client-authorization`, caForm);
      toast.success("Autorización contractual registrada");
      setCaModal(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };
  const downloadPdf = async (g) => {
    const res = await api.get(`/generators/${g.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
    const a = document.createElement("a");
    a.href = url; a.download = `${g.folio}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const chip = (s) => {
    const [label, color] = STATUS_META[s] || [s, "var(--cyan)"];
    return <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-sm" style={{ border: `1px solid ${color}`, color }}>{label}</span>;
  };

  return (
    <div className="space-y-3" data-testid="generators-tab">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <span className="label-mono" style={{ color: "var(--cyan)" }}>// NÚMEROS GENERADORES · residente captura → supervisión revisa → dirección autoriza</span>
        <div className="flex gap-2 items-center">
          <select data-testid="gen-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="input-cyber px-3 py-1.5 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos</option>
            {Object.entries(STATUS_META).map(([k, [l]]) => <option key={k} value={k} style={{ background: "#0a0f1d" }}>{l}</option>)}
          </select>
          {canCapture && <button data-testid="new-generator-btn" onClick={startNew} className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"><Plus className="w-3 h-3" /> Generador</button>}
        </div>
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[1100px]" data-testid="generators-table">
          <thead className="panel-header"><tr className="text-left">
            <th className="label-mono px-3 py-3">Folio</th><th className="label-mono px-3 py-3">Fecha</th>
            <th className="label-mono px-3 py-3">Concepto</th><th className="label-mono px-3 py-3">Ubicación</th>
            <th className="label-mono px-3 py-3 text-right">Cantidad</th><th className="label-mono px-3 py-3 text-right">Importe</th>
            <th className="label-mono px-3 py-3">Estado</th><th className="label-mono px-3 py-3">Aut. cliente</th>
            <th className="label-mono px-3 py-3 text-right">Acciones</th>
          </tr></thead>
          <tbody>
            {gens.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-slate-500">Sin generadores{filter ? ` (${filter})` : ""}.</td></tr>}
            {gens.map((g) => (
              <tr key={g.id} className="row-hover align-top" style={{ borderBottom: "1px solid var(--border)" }}>
                <td className="px-3 py-2.5 font-mono text-xs font-bold" style={{ color: "var(--cyan)" }}>{g.folio}
                  {g.estimacion_id ? <div className="text-[9px] text-slate-500">EN ESTIMACIÓN</div>
                    : (g.qty_estimated > 0 && <div className="text-[9px]" style={{ color: "#eab308" }}>PARCIAL EST. {g.qty_estimated}</div>)}
                </td>
                <td className="px-3 py-2.5 font-mono text-xs">{g.date}</td>
                <td className="px-3 py-2.5 text-xs text-slate-200 max-w-[300px]">
                  {g.code && <span className="font-mono text-slate-400">{g.code} · </span>}{g.description}
                  {g.is_extraordinary && <span className="ml-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-sm" style={{ background: "rgba(249,115,22,0.18)", color: "#f97316" }}>EXTRA</span>}
                  <div className="text-[10px] text-slate-500">Capturó: {g.created_by_name}</div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-400 max-w-[160px]">{g.location || "—"}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{g.quantity} {g.unit}</td>
                <td className="px-3 py-2.5 text-right font-mono text-xs">{fmtMXN(g.amount)}</td>
                <td className="px-3 py-2.5">{chip(g.status)}{g.rejected_reason && <div className="text-[10px] mt-0.5" style={{ color: "var(--red)" }}>{g.rejected_reason}</div>}</td>
                <td className="px-3 py-2.5 text-xs">{g.client_authorization?.authorized ? <span style={{ color: "var(--green)" }}>{g.client_authorization.reference}</span> : <span className="text-slate-600">—</span>}</td>
                <td className="px-3 py-2.5 text-right whitespace-nowrap">
                  {g.photos?.length > 0 && <IconBtn testId={`gen-photos-${g.id}`} onClick={() => setGallery({ photos: g.photos, title: g.folio })} color="var(--cyan)"><Camera className="w-4 h-4" /></IconBtn>}
                  <IconBtn testId={`gen-pdf-${g.id}`} onClick={() => downloadPdf(g)} color="var(--text-dim)"><FileDown className="w-4 h-4" /></IconBtn>
                  {g.status === "capturado" && isSupervisor && project?.supervisor_id === user?.id && (
                    <button data-testid={`gen-review-${g.id}`} onClick={() => doAction(g, "review", {})} className="text-xs font-semibold hover:underline mr-2" style={{ color: "var(--cyan)" }}>REVISAR</button>
                  )}
                  {((g.status === "revisado") || (g.status === "capturado" && !project?.supervisor_id)) && isAdmin && (
                    <button data-testid={`gen-authorize-${g.id}`} onClick={() => doAction(g, "authorize", {})} className="text-xs font-semibold hover:underline mr-2 inline-flex items-center gap-1" style={{ color: "var(--green)" }}><CheckCircle2 className="w-3.5 h-3.5" /> AUTORIZAR</button>
                  )}
                  {["capturado", "revisado"].includes(g.status) && (isSupervisor || isAdmin) && (
                    <IconBtn testId={`gen-reject-${g.id}`} onClick={() => reject(g)} color="var(--red)"><XCircle className="w-4 h-4" /></IconBtn>
                  )}
                  {canManage && !g.client_authorization?.authorized && (
                    <IconBtn testId={`gen-client-auth-${g.id}`} onClick={() => { setCaModal(g); setCaForm({ reference: "", date: todayLocal(), authorized_by_name: "", notes: "" }); }} color="#f97316" title="Registrar autorización del cliente"><Stamp className="w-4 h-4" /></IconBtn>
                  )}
                  {isAdmin && g.status !== "autorizado" && !g.estimacion_id && (
                    <IconBtn testId={`gen-delete-${g.id}`} onClick={() => remove(g)} color="var(--red)"><Trash2 className="w-4 h-4" /></IconBtn>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {openNew && form && (
        <Modal onClose={() => setOpenNew(false)} title="Nuevo número generador">
          <form onSubmit={save} className="space-y-4">
            <div className="flex gap-2">
              {[false, true].map((ex) => (
                <button key={String(ex)} type="button" data-testid={ex ? "gen-type-extra" : "gen-type-catalog"} onClick={() => setForm({ ...form, is_extraordinary: ex })}
                  className="px-3 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex-1"
                  style={{ background: form.is_extraordinary === ex ? "rgba(34,211,238,0.12)" : "transparent", color: form.is_extraordinary === ex ? "var(--cyan)" : "var(--text-dim)", border: `1px solid ${form.is_extraordinary === ex ? "var(--cyan)" : "var(--border)"}` }}>
                  {ex ? "Concepto extraordinario" : "Concepto del catálogo"}
                </button>
              ))}
            </div>
            {!form.is_extraordinary ? (
              <Sel label="Concepto contratado" testId="gen-budget-item" value={form.budget_item_id} onChange={(v) => setForm({ ...form, budget_item_id: v })}
                options={budgetItems.map((b) => ({ value: b.id, label: `${b.code || ""} ${b.description?.slice(0, 90)}` }))} />
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <In label="Clave (opcional)" testId="gen-code" value={form.code} onChange={(v) => setForm({ ...form, code: v })} required={false} />
                  <In label="Unidad" testId="gen-unit" value={form.unit} onChange={(v) => setForm({ ...form, unit: v })} />
                </div>
                <In label="Descripción del concepto extraordinario" testId="gen-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
                <In label="Precio unitario propuesto MXN" testId="gen-unit-price" type="number" value={form.unit_price} onChange={(v) => setForm({ ...form, unit_price: v })} required={false} />
                <p className="text-xs" style={{ color: "#f97316" }}>Los extraordinarios NO son cobrables hasta registrar la autorización del cliente o convenio.</p>
              </>
            )}
            <div className="grid grid-cols-2 gap-3">
              <In label="Fecha de ejecución" testId="gen-date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} />
              <In label="Ubicación (eje/tramo/nivel)" testId="gen-location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} required={false} />
            </div>
            <div className="space-y-2">
              <span className="label-mono">Medidas (largo×ancho×alto×piezas) o cantidad directa</span>
              {form.measurements.map((m, idx) => (
                <div key={idx} className="grid grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_52px_28px] gap-1.5 items-center">
                  <input data-testid={`gen-m-label-${idx}`} placeholder="Elemento/eje" value={m.label} onChange={(e) => upRow(idx, "label", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <input data-testid={`gen-m-length-${idx}`} placeholder="Largo" type="number" step="any" value={m.length} onChange={(e) => upRow(idx, "length", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <input data-testid={`gen-m-width-${idx}`} placeholder="Ancho" type="number" step="any" value={m.width} onChange={(e) => upRow(idx, "width", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <input data-testid={`gen-m-height-${idx}`} placeholder="Alto" type="number" step="any" value={m.height} onChange={(e) => upRow(idx, "height", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <input data-testid={`gen-m-pieces-${idx}`} placeholder="Pzas" type="number" step="any" value={m.pieces} onChange={(e) => upRow(idx, "pieces", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <input data-testid={`gen-m-qty-${idx}`} placeholder="Cant." type="number" step="any" value={m.qty} onChange={(e) => upRow(idx, "qty", e.target.value)} className="input-cyber px-2 py-1.5 text-xs min-w-0 w-full" />
                  <span className="font-mono text-xs text-right" style={{ color: "var(--cyan)" }}>{rowQty(m) ? rowQty(m).toFixed(3) : ""}</span>
                  <button type="button" onClick={() => setForm({ ...form, measurements: form.measurements.filter((_, i) => i !== idx) })} disabled={form.measurements.length === 1} className="p-1 rounded-sm hover:bg-white/5 disabled:opacity-30" style={{ color: "var(--red)" }}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              <div className="flex justify-between items-center">
                <button type="button" data-testid="gen-add-row" onClick={() => setForm({ ...form, measurements: [...form.measurements, { ...emptyRow }] })} className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1"><Plus className="w-3 h-3" /> Renglón</button>
                <span className="text-sm">Total: <span className="font-mono font-bold" style={{ color: "var(--cyan)" }} data-testid="gen-total-qty">{totalQty.toFixed(3)}</span></span>
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1.5 flex items-center gap-1"><Camera className="w-3.5 h-3.5" /> Fotografías / croquis / evidencias</label>
              <input data-testid="gen-photos-input" type="file" accept="image/*" multiple onChange={(e) => setFiles([...e.target.files])} className="text-xs text-slate-400" />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setOpenNew(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" disabled={saving} data-testid="save-generator-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">Capturar generador</button>
            </div>
          </form>
        </Modal>
      )}

      {caModal && (
        <Modal onClose={() => setCaModal(null)} title={`Autorización contractual · ${caModal.folio}`}>
          <form onSubmit={saveClientAuth} className="space-y-4">
            <p className="text-xs text-slate-400">Registra la autorización del cliente o dependencia (oficio, convenio, minuta). Es independiente de la autorización interna de Dirección.</p>
            <In label="Referencia (oficio/convenio)" testId="ca-reference" value={caForm.reference} onChange={(v) => setCaForm({ ...caForm, reference: v })} />
            <div className="grid grid-cols-2 gap-3">
              <In label="Fecha" testId="ca-date" type="date" value={caForm.date} onChange={(v) => setCaForm({ ...caForm, date: v })} required={false} />
              <In label="Autorizó (nombre)" testId="ca-by" value={caForm.authorized_by_name} onChange={(v) => setCaForm({ ...caForm, authorized_by_name: v })} required={false} />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Notas</label>
              <textarea value={caForm.notes} onChange={(e) => setCaForm({ ...caForm, notes: e.target.value })} rows={2} className="w-full input-cyber px-3 py-2 text-sm" />
            </div>
            <div className="flex gap-2 justify-end pt-3" style={{ borderTop: "1px solid var(--border)" }}>
              <button type="button" onClick={() => setCaModal(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" data-testid="save-client-auth-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1.5"><FileCheck className="w-3.5 h-3.5" /> Registrar</button>
            </div>
          </form>
        </Modal>
      )}

      {gallery && <EvidenceGallery photos={gallery.photos} title={gallery.title} onClose={() => setGallery(null)} />}
    </div>
  );

  function upRow(idx, key, val) {
    const measurements = [...form.measurements];
    measurements[idx] = { ...measurements[idx], [key]: val };
    setForm({ ...form, measurements });
  }
}

const IconBtn = ({ children, onClick, color, testId, title }) => (
  <button data-testid={testId} title={title} onClick={onClick} className="p-1 rounded-sm hover:bg-white/5 mr-1.5" style={{ color }}>{children}</button>
);
